import { useCallback } from 'react';

import { useDispatch } from 'react-redux';

import { setDialogAlert, removeDialogAlert, clearDialogAlerts } from '@/redux/dialogAlerts/actions';
import { DialogAlert } from '@/shared/types/dialogAlert';
import { capitalizeFirstLetter } from '@/util/util';

type UseDialogAlertParams = {
  id: string;
  nodeId: string;
  title: string;
  type: DialogAlert['type'];
  index?: number;
};

type FieldAlertOpts = { cardIndex?: number; buttonIndex?: number };

const useDialogAlert = ({ id, nodeId, title, type, index }: UseDialogAlertParams) => {
  const dispatch = useDispatch();

  const setFieldAlert = useCallback(
    (
      alertType: DialogAlert['alertType'],
      alertField: string,
      body: string | undefined,
      opts?: FieldAlertOpts
    ) => {
      if (!body) {
        dispatch(removeDialogAlert({ id, alertField, ...opts }));
        return;
      }
      dispatch(
        setDialogAlert({
          alertType,
          id,
          nodeId,
          title,
          body: capitalizeFirstLetter(body),
          type,
          alertField,
          ...(index !== undefined && { index }),
          ...opts,
        })
      );
    },
    [dispatch, id, nodeId, title, type, index]
  );

  const setFieldError = useCallback(
    (alertField: string, body: string | undefined, opts?: FieldAlertOpts) =>
      setFieldAlert('error', alertField, body, opts),
    [setFieldAlert]
  );

  // Surfaces a soft verdict (e.g. guideline recommendations) in the dialog-alerts panel
  // without blocking save — only `error` alerts gate saves via `hasDialogAnyErrors`.
  const setFieldWarning = useCallback(
    (alertField: string, body: string | undefined, opts?: FieldAlertOpts) =>
      setFieldAlert('warning', alertField, body, opts),
    [setFieldAlert]
  );

  const clearAlerts = useCallback(() => {
    dispatch(clearDialogAlerts({ id }));
  }, [dispatch, id]);

  return { setFieldError, setFieldWarning, clearAlerts };
};

export default useDialogAlert;
